import { Col, FormGroup, Label } from 'reactstrap';
import * as Utils from './utils';
import CheckboxV2Field from './fields/checkboxV2.field';
import SwitchV2Field from './fields/switchV2.field';
import PasswordV2Field from './fields/passwordV2.field';
import PhoneV2Field from './fields/phoneV2.field';
import SelectV2Field from './fields/selectV2.field';
import TextV2Field from './fields/textV2.field';
import CodeV2Field from './fields/codeV2.field';
import SSNV2Field from './fields/ssnV2.field';
import RadioV2Field from './fields/radioV2.field';
import { useMemo } from 'react';
import FormV2InfoTooltip from './info-tooltip.component';
import DateV2Field from './fields/dateV2.field';

const labelHidden = ['checkbox', 'switch', 'radio'];
const messageHidden = [];

/**
 * @type {Record<import('./types').FormV2Types, (React.FC<import('./types').FormV2FieldProps> | null)>}
 */
const components = {
    checkbox: CheckboxV2Field,
    code: CodeV2Field,
    custom: null,
    date: TextV2Field,
    email: TextV2Field,
    hidden: null,
    number: TextV2Field,
    password: PasswordV2Field,
    phone: PhoneV2Field,
    radio: RadioV2Field,
    select: SelectV2Field,
    ssn: SSNV2Field,
    switch: SwitchV2Field,
    text: TextV2Field,
    textarea: TextV2Field,
    url: TextV2Field,
};

/**
 * @typedef {{
 *   type: import('./types').FormV2Types;
 *   id: string;
 *   name: string;
 *   handleChange: (obj: {value: any; isRequired: boolean; options?: any[]}) => void;
 *   handleBlur: (obj: {isRequired: boolean; options?: any;}) => void;
 *   fieldState: {
 *     value: any;
 *     hasError: boolean;
 *     isTouched: boolean;
 *     isValid: boolean;
 *     errorMessage: string;
 *   };
 *   isRequired?: boolean;
 *   isDisabled?: boolean;
 *   styles?: any;
 *   classes?: any;
 *   options?: any[];
 *   cols?: import('./types').Cols | {sm?: import('./types').Cols; md?: import('./types').Cols; lg?: import('./types').Cols; xl?: import('./types').Cols; xxl?: import('./types').Cols} | ((state: Record<string, FormV2State>, externalState: Record<string, any>) => (import('./types').Cols | {sm?: import('./types').Cols; md?: import('./types').Cols; lg?: import('./types').Cols; xl?: import('./types').Cols; xxl?: import('./types').Cols}));
 *   label?: string;
 *   description?: string;
 *   info?: string;
 *   placeholder?: string;
 *   hideErrorMessage?: boolean;
 * }} SingleFieldProps
 */

/**
 * 
 * @param {{
 *   data: import("./types").FormV2FormData;
 *   fieldName: string;
 *   refresh: () => void;
 *   lang: string;
 *   isSubmitting?: boolean;
 *   field?: import("./types").FormV2Field;
 *   _singleField?: SingleFieldProps;
 * }} param0 
 */
const Field = ({
    data,
    field,
    fieldName,
    refresh,
    lang,
    isSubmitting,

    _singleField
}) => {
    const isRequired = _singleField ? (_singleField.isRequired ?? false) : Utils.getIsRequired(field?.isRequired, data.state, data.externalState);
    const isDisabled = isSubmitting ? true : _singleField ? (_singleField.isDisabled ?? false) : Utils.getIsDisabled(field?.isDisabled, data.state, data.externalState);
    const styles = _singleField ? (_singleField.styles ?? {}) : Utils.getStyles(field?.styles, data.state, data.externalState);
    const classes = _singleField ? (_singleField.classes ?? {}) : Utils.getClassNames(field?.classes, data.state, data.externalState);
    const options = _singleField ? (_singleField.options ?? undefined) : Utils.getOptions(field?.config?.options, data.state, data.externalState);
    const cols = Utils.getCols(_singleField ? _singleField.cols : field?.cols, data.state, data.externalState);

    const label = _singleField ? (_singleField.label ?? '') : Utils.getLabel(field?.label, data.state, data.externalState);
    const description = _singleField ? (_singleField.description ?? '') : Utils.getDescription(field?.description, data.state, data.externalState);
    let info = _singleField ? (_singleField.info ?? '') : Utils.getDescription(field?.info, data.state, data.externalState);
    if (info) info = info.translate(lang);
    const placeholder = _singleField ? (_singleField.placeholder ?? '') : Utils.getPlaceholder(field?.placeholder, data.state, data.externalState);

    const hideErrorMessage = _singleField ? (_singleField.hideErrorMessage ?? false) : Utils.getHideErrorMessage(field?.config?.hideErrorMessage, data.state, data.externalState);

    const fieldState = _singleField ? _singleField.fieldState : data.state?.[fieldName];
    if (!fieldState) return null;

    const id = _singleField ? _singleField.id : field?.id;
    const type = _singleField ? _singleField.type : field?.type;
    if (!type) return null;

    let errorMessage = hideErrorMessage ? null : fieldState.hasError ? fieldState.errorMessage : null;
    if (errorMessage) {
        errorMessage = errorMessage.translate(lang).replace('{min}', field?.config?.min || '').replace('{max}', field?.config?.max || '');
        if (data?.externalState?.ageLimit && typeof data?.externalState?.ageLimit === 'number') {
            const ageLimit = data?.externalState?.ageLimit;
            errorMessage = errorMessage.replace(/{AGE_LIMIT}/g, ageLimit).replace(/{AGE_LIMIT-1}/g, ageLimit - 1);
        }
    }

    const aria = Utils.getAria(errorMessage, description, id, messageHidden.includes(type) ? true : false, fieldState.isTouched, fieldState.isValid);

    const ssnLabel = useMemo(() => {
        if (type === 'ssn') {
            const country = fieldState?.value?.country;
            const value = fieldState?.value?.ssn;

            if (country) {
                let placeholder = Utils.ssnFormats?.[country];
                if (country === 'sweden' && lang === 'se') placeholder = 'ÅÅÅÅMMDDNNNN';
                if (placeholder) {
                    return <span className='formV2-field-label-ssn'>
                        <span>{'Social security number'.translate(lang)}</span>
                        {' '}
                        <span>{'('}</span>
                        <span>{placeholder.slice(0, (value?.length ?? 0))}</span>
                        {placeholder.slice((value?.length ?? 0), placeholder.length)}
                        <span>{')'}</span>
                    </span>
                }
            }

            return 'Social security number'.translate(lang);
        }

        return null;
    }, [fieldState, lang, type]);

    const onChange = (value) => {
        if (isDisabled) return;
        if (field?.transformers?.instant) value = field?.transformers.instant(value, data.state, data.externalState, field?.config, options);
        if (field?.transformers?.customInstant) value = field?.transformers.customInstant(value, data.state, data.externalState, field?.config, options);

        if (value === fieldState.value) return;

        if (_singleField) {
            _singleField.handleChange?.({
                value,
                isRequired,
                options,
            })
        } else {
            data.handleChange?.({
                name: fieldName,
                value,
                isRequired,
                options,
                type: type,
            });
        }

        field?.callbacks?.onChange?.(value, data.state, data.externalState, field?.config, options);
    };

    const onBlur = () => {
        if (_singleField) {
            data.handleBlur?.({
                isRequired,
                options,
            });
        } else {
            data.handleBlur?.({
                name: fieldName,
                isRequired,
                options,
            });
        }

        field?.callbacks?.onBlur?.(fieldState.value, data.state, data.externalState, field?.config, options);
    };

    const onFocus = () => {
        field?.callbacks?.onFocus?.(fieldState.value, data.state, data.externalState, field?.config, options);
    };

    let Component = components[type];
    if (type === 'custom') Component = field?.component;

    const BeforeComponent = field?.config?.beforeComponent;
    const AfterComponent = field?.config?.afterComponent;

    if (!Component) return null;
    return <Col
        {...cols}
        xs={cols?.xs ?? cols?.sm}
        className={type === 'radio' ? undefined : classes?.container}
        style={type === 'radio' ? undefined : styles?.container}
    >
        {BeforeComponent ? (
            <BeforeComponent
                key={id + '-before'}
                id={id}
                name={_singleField ? (_singleField.name ?? id) : fieldName}
                type={type}
                data={data}
                field={field ?? _singleField}
                fieldName={fieldName}
                hasError={fieldState.hasError}
                isDisabled={isDisabled}
                isRequired={isRequired}
                isTouched={fieldState.isTouched}
                isValid={fieldState.isValid}
                valid={data.config?.showValidFields && fieldState.isValid ? true : undefined}
                lang={lang}
                fieldLang={field?.config?.lang}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                refresh={refresh}
                value={fieldState.value}
                options={options}
                classes={classes}
                styles={styles}
                errorMessage={errorMessage}
                placeholder={placeholder}
                aria={aria}
                label={label}
                info={info}
                isSubmitting={isSubmitting}
            />
        ) : null}
        <FormGroup tag={field?.config?.formFieldTag ?? (type === 'radio' ? 'fieldset' : undefined)}>
            {(label || ssnLabel) && !labelHidden.includes(type) ? (
                <Label
                    htmlFor={id}
                    className={`formV2-field-label ${classes?.label ?? ''}`}
                >
                    {field?.config?.lang ? <strong>
                        {'['}
                        {field?.config?.lang}
                        {'] '}
                    </strong> : null}
                    {ssnLabel ? ssnLabel : label.translate(lang)}
                    {isRequired ? <span aria-hidden style={{ color: 'red' }}>*</span> : null}
                    {info ? <FormV2InfoTooltip info={info} className={classes?.info} style={styles?.info} /> : null}
                </Label>
            ) : (
                null
            )}
            {type === 'radio' && label ? (
                <legend className={`formV2-field-label ${classes?.label ?? ''}`}>
                    {field?.config?.lang ? <strong>
                        {'['}
                        {field?.config?.lang}
                        {'] '}
                    </strong> : null}
                    {label.translate(lang)}
                    {isRequired ? <span aria-hidden style={{ color: 'red' }}>*</span> : null}
                    {info ? <FormV2InfoTooltip info={info} className={classes?.info} style={styles?.info} /> : null}
                </legend>
            ) : null}

            <Component
                key={id}
                id={id}
                name={_singleField ? (_singleField.name ?? id) : fieldName}
                type={type}
                data={data}
                field={field ?? _singleField}
                fieldName={fieldName}
                hasError={fieldState.isTouched && fieldState.hasError ? true : false}
                isDisabled={isDisabled}
                isRequired={isRequired}
                isTouched={fieldState.isTouched}
                isValid={fieldState.isValid}
                valid={data.config?.showValidFields && fieldState.isTouched && fieldState.isValid ? true : undefined}
                lang={lang}
                fieldLang={field?.config?.lang}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                refresh={refresh}
                value={fieldState.value}
                options={options}
                classes={classes}
                styles={styles}
                errorMessage={errorMessage}
                placeholder={placeholder}
                aria={aria}
                label={label}
                info={info}
                isSubmitting={isSubmitting}
            />

            {(errorMessage || description) && id && !messageHidden.includes(type) ? (
                <p
                    id={id + '-message'}
                    className={`formV2-field-message ${errorMessage ? 'formV2-field-message-error' : ''} ${errorMessage ? (classes?.error ?? '') : (classes?.description ?? '')} ${type === 'checkbox' ? 'formV2-field-message-checkbox' : type === 'switch' ? 'formV2-field-message-switch' : ''}`}
                    role={errorMessage ? 'alert' : undefined}
                >
                    {errorMessage || description?.translate(lang)}
                </p>
            ) : (
                null
            )}

            {AfterComponent ? (
                <AfterComponent
                    key={id + '-after'}
                    id={id}
                    name={_singleField ? (_singleField.name ?? id) : fieldName}
                    type={type}
                    data={data}
                    field={field ?? _singleField}
                    fieldName={fieldName}
                    hasError={fieldState.hasError}
                    isDisabled={isDisabled}
                    isRequired={isRequired}
                    isTouched={fieldState.isTouched}
                    isValid={fieldState.isValid}
                    valid={data.config?.showValidFields && fieldState.isValid ? true : undefined}
                    lang={lang}
                    fieldLang={field?.config?.lang}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    refresh={refresh}
                    value={fieldState.value}
                    options={options}
                    classes={classes}
                    styles={styles}
                    errorMessage={errorMessage}
                    placeholder={placeholder}
                    aria={aria}
                    label={label}
                    info={info}
                    isSubmitting={isSubmitting}
                />
            ) : null}
        </FormGroup>
    </Col>
}

export default Field;